import React from 'react';
import { useContext } from 'react';
import { connect, DispatchProp } from 'react-redux';

import { WeightNumber, Link } from '@dndbeyond/character-common-components/es';
import {
    Collapsible,
    CollapsibleHeaderContent,
    CollapsibleHeading,
    ItemName,
} from '@dndbeyond/character-components/es';
import {
    ApiAdapterPromise,
    ApiAdapterRequestConfig,
    ApiResponse,
    rulesEngineSelectors,
    BaseItemDefinitionContract,
    Item,
    Modifier,
    RuleData,
    TypeValueLookup,
    CampaignUtils,
    CharacterTheme,
    Container,
    ContainerUtils,
    InventoryManager,
    Constants,
    serviceDataSelectors,
    PartyInfo,
    ItemManager,
} from '@dndbeyond/character-rules-engine/es';

import { sidebarActions } from '../../../actions';
import { CustomItemCreator } from '../../../components/CustomItemCreator';
import EquipmentShop from '../../../components/EquipmentShop';
import ItemDetail from '../../../components/ItemDetail';
import { SidebarHeader } from '../../../components/Sidebar';
import { RemoveButtonWithModal, ThemeButtonWithMenu } from '../../../components/common/Button';
import { InventoryManagerContext } from '../../../managers/InventoryManagerContext';
import { apiCreatorSelectors } from '../../../selectors';
import { SharedAppState } from '../../../stores/typings';
import { AppNotificationUtils, PaneComponentEnum } from '../../../utils';
import { ItemSlotManager } from '../../../components/ItemSlotManager';

interface Props extends DispatchProp {
    loadAvailableItems: (
        additionalConfig?: Partial<ApiAdapterRequestConfig>,
    ) => ApiAdapterPromise<ApiResponse<Array<BaseItemDefinitionContract>>>;
    ruleData: RuleData;
    globalModifiers: Array<Modifier>;
    valueLookupByType: TypeValueLookup;
    proficiencyBonus: number;
    containers: Array<Container>;
    theme: CharacterTheme;
    partyInfo: PartyInfo | null;
    inventoryManager: InventoryManager;
}

class EquipmentManagePane extends React.PureComponent<Props> {
    handleItemAdd = (item: Item, amount: number, containerDefinitionKey: string): void => {
        const { inventoryManager } = this.props;

        inventoryManager.handleAdd(
            { item, amount, containerDefinitionKey },
            AppNotificationUtils.handleItemAddAccepted.bind(this, item, amount),
            AppNotificationUtils.handleItemAddRejected.bind(this, item, amount),
        );
    };

    renderInventory = (inventory: Array<ItemManager>): React.ReactNode => {
        const { theme, ruleData, proficiencyBonus, inventoryManager, partyInfo, containers } = this.props;

        return inventory.map((item) => {
            const isContainer = item.isContainer();
            const container = inventoryManager.getContainer(item.generateContainerDefinitionKey());

            const calloutButton: React.ReactNode = isContainer ? (
                <RemoveButtonWithModal
                    className="ct-item-detail__action"
                    style={'outline'}
                    size="small"
                    modalTitle={`Remove ${item.getName()}?`}
                    modalContent={`Removing the ${item.getName()} will also remove all of its${
                        (container && container.hasInfusions()) ? ' infusions and' : ''
                    } contents.`}
                    confirmCallback={item.handleRemove}
                />
            ) : (
                <ThemeButtonWithMenu
                    showSingleOption={true}
                    containerEl={document.querySelector('.ct-sidebar__portal') as HTMLElement}
                    groupedOptions={ContainerUtils.getGroupedOptions(
                        item.getContainerDefinitionKey(),
                        containers,
                        'Move To:',
                        partyInfo ? CampaignUtils.getSharingState(partyInfo) : Constants.PartyInventorySharingStateEnum.OFF,
                    )}
                    buttonStyle="outline"
                    onSelect={(containerDefinitionKey) => item.handleMove({ containerDefinitionKey })}
                >
                    Move
                </ThemeButtonWithMenu>
            );

            return (
                <Collapsible
                    key={item.getUniqueKey()}
                    className={`ct-equipment-manage-pane__item${
                        item.isContainer() ? ' ct-equipment-manage-pane__item--is-container' : ''
                    }`}
                    layoutType={'minimal'}
                    header={
                        <CollapsibleHeaderContent
                            heading={
                                <div className="ct-equipment-manage-pane__item-header">
                                    <div className="ct-equipment-manage-pane__item-header-action">
                                        <ItemSlotManager
                                            isUsed={!!item.isEquipped()}
                                            theme={theme}
                                            canUse={inventoryManager.canEquipUnequipItem(item.item)}
                                            onSet={(uses) => {
                                                //TODO need different component than SlotManager for item equipped/unequipped
                                                if (uses === 0) {
                                                    item.handleUnequip();
                                                }

                                                if (uses === 1) {
                                                    item.handleEquip();
                                                }
                                            }}
                                            useTooltip={false}
                                            showEmptySlot={!isContainer}
                                        />
                                    </div>
                                    <div
                                        className={`ct-equipment-manage-pane__item-header-name${
                                            isContainer
                                                ? ' ct-equipment-manage-pane__item-header-name--is-container'
                                                : ''
                                        }`}
                                    >
                                        {isContainer ? 'Container details' : <ItemName item={item.item} theme={theme} />}
                                    </div>
                                </div>
                            }
                            callout={calloutButton}
                        />
                    }
                >
                    <ItemDetail
                        theme={theme}
                        item={item.item}
                        ruleData={ruleData}
                        showCustomize={false}
                        showImage={false}
                        proficiencyBonus={proficiencyBonus}
                    />
                </Collapsible>
            );
        });
    };

    renderContainers = (): React.ReactNode => {
        const { theme, inventoryManager } = this.props;

        return inventoryManager.getAllContainers().map((container) => {
            const containerInventory = container.getInventoryItems({includeContainer:true});

            const inventoryLength = containerInventory.length;
            let isEmpty: boolean = inventoryLength === 0;

            let headerNode: React.ReactNode = (
                <CollapsibleHeaderContent
                    heading={<CollapsibleHeading>{`${container.getName()} (${inventoryLength})`}</CollapsibleHeading>}
                    callout={
                        <div className="ct-equipment-manage-pane__callout">
                            <WeightNumber
                                number={container.getWeightInfo().total}
                                isDarkMode={theme.isDarkMode}
                            />
                        </div>
                    }
                />
            );

            return (
                <Collapsible
                    header={headerNode}
                    key={container.getDefinitionKey()}
                >
                    <div className="ct-equipment-manage-pane__inventory">
                        {this.renderInventory(containerInventory)}

                        {isEmpty && (
                            <div className="ct-equipment-manage-pane__empty-container">
                                There are no items in your {container.getName()}
                            </div>
                        )}
                    </div>
                </Collapsible>
            );
        });
    };

    render() {
        const {
            loadAvailableItems,
            globalModifiers,
            valueLookupByType,
            ruleData,
            proficiencyBonus,
            containers,
            theme,
            partyInfo,
            dispatch,
        } = this.props;

        return (
            <div className="ct-equipment-manage-pane">
                <SidebarHeader>Manage Inventory</SidebarHeader>
                <Collapsible header="Add Items" initiallyCollapsed={false}>
                    <EquipmentShop
                        partyInfo={partyInfo}
                        containers={containers}
                        theme={theme}
                        loadItems={loadAvailableItems}
                        globalModifiers={globalModifiers}
                        valueLookupByType={valueLookupByType}
                        onItemAdd={this.handleItemAdd}
                        ruleData={ruleData}
                        proficiencyBonus={proficiencyBonus}
                    />
                    <CustomItemCreator containers={containers} />
                </Collapsible>

                {/*container collapsibles*/}
                {this.renderContainers()}

                <div className="ct-equipment-manage-pane__links">
                    <div className="ct-equipment-manage-pane__link">
                        <Link useTheme={true} onClick={() => dispatch(sidebarActions.paneHistoryPush(PaneComponentEnum.STARTING_EQUIPMENT))}>
                            Starting Equipment
                        </Link>
                    </div>
                    <div className="ct-equipment-manage-pane__link">
                        <Link useTheme={true} onClick={() => dispatch(sidebarActions.paneHistoryPush(PaneComponentEnum.CURRENCY))}>
                            Currency
                        </Link>
                    </div>
                    <div className="ct-equipment-manage-pane__link">
                        <Link useTheme={true} onClick={() => dispatch(sidebarActions.paneHistoryPush(PaneComponentEnum.ENCUMBRANCE))}>
                            Encumbrance
                        </Link>
                    </div>
                </div>
            </div>
        );
    }
}

function mapStateToProps(state: SharedAppState) {
    return {
        loadAvailableItems: apiCreatorSelectors.makeLoadAvailableItems(state),
        globalModifiers: rulesEngineSelectors.getValidGlobalModifiers(state),
        ruleData: rulesEngineSelectors.getRuleData(state),
        valueLookupByType: rulesEngineSelectors.getCharacterValueLookupByType(state),
        proficiencyBonus: rulesEngineSelectors.getProficiencyBonus(state),
        containers: rulesEngineSelectors.getInventoryContainers(state),
        theme: rulesEngineSelectors.getCharacterTheme(state),
        partyInfo: serviceDataSelectors.getPartyInfo(state),
    };
}

function EquipmentManagePaneContainer(props) {
    const { inventoryManager } = useContext(InventoryManagerContext);
    return <EquipmentManagePane inventoryManager={inventoryManager} {...props} />;
}

export default connect(mapStateToProps)(EquipmentManagePaneContainer);
